<script setup>
import { defineProps, reactive, onMounted, onUnmounted, defineEmits, ref, computed } from 'vue';
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { Bars3Icon, BellIcon, XMarkIcon, ArrowLeftIcon, PlayIcon, PauseIcon, SquaresPlusIcon } from '@heroicons/vue/24/outline'
import imagotipo from '../../../assets/images/logo/imagotipo_white.svg'
import HeroIcon from '@/Components/UI/HeroIcon.vue'
import { useI18n } from "vue-i18n"
import { useUserStore } from '@/stores/user'
import { useNotificationsStore } from '@/stores/notifications'
import { useLayoutStore } from '@/stores/layout'
import NotificationSidebar from '@/Components/Notifications/NotificationSidebar.vue';
import notificationsApi from '@/services/api/notifications/notifications';

const { t } = useI18n();
const emit = defineEmits(['attendanceChanged']);

const userStore = useUserStore();
const notificationsStore = useNotificationsStore();
const layoutStore = useLayoutStore();
const showNotifSidebar = ref(false);
const isSupportChatLoading = ref(false);

const GHL_CHAT_SCRIPT_SRC = 'https://widgets.leadconnectorhq.com/loader.js';
const GHL_CHAT_RESOURCES_URL = 'https://widgets.leadconnectorhq.com/chat-widget/loader.js';
const GHL_CHAT_WIDGET_ID = '68e8dbada9accb4c86bf8fa3';
const GHL_HIDE_LAUNCHER_STYLE_ID = 'ghl-hide-launcher-style';
const SUPPORT_BOX_BOTTOM_DESKTOP = '0px';
const SUPPORT_BOX_BOTTOM_MOBILE = '0px';
let supportLauncherObserver = null;
let supportLauncherInterval = null;

const waitForSupportChatWidget = (timeoutMs = 5000) => {
  if (window.leadConnector?.chatWidget) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const intervalId = setInterval(() => {
      if (window.leadConnector?.chatWidget) {
        clearInterval(intervalId);
        resolve();
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        clearInterval(intervalId);
        reject(new Error('Support chat widget not available'));
      }
    }, 100);
  });
};

const hideSupportLauncherElementsInRoot = (root) => {
  if (!root?.querySelectorAll) {
    return;
  }

  const launcherSelectors = [
    '.lc_text-widget--bubble',
    '[class*="lc_text-widget--bubble"]',
    '.widget-open-icon',
    '.icon.widget-open-icon',
    '[class*="widget-open-icon"]',
    '.lc_text_widget-trigger',
    '[class*="lc_text_widget-trigger"]',
    '.lc-chat-widget',
    '[data-testid="chat-widget-launcher"]',
    '[class*="chat-widget-launcher"]',
  ];

  launcherSelectors.forEach((selector) => {
    root.querySelectorAll(selector).forEach((element) => {
      element.style.setProperty('display', 'none', 'important');
      element.style.setProperty('visibility', 'hidden', 'important');
      element.style.setProperty('opacity', '0', 'important');
      element.style.setProperty('pointer-events', 'none', 'important');
    });
  });
};

const positionSupportWidgetBoxInRoot = (root) => {
  if (!root?.querySelectorAll) {
    return;
  }

  const widgetBoxSelectors = [
    '.lc_text-widget--box',
    '[class*="lc_text-widget--box"]',
  ];

  const targetBottom = window.innerWidth <= 768 ? SUPPORT_BOX_BOTTOM_MOBILE : SUPPORT_BOX_BOTTOM_DESKTOP;

  widgetBoxSelectors.forEach((selector) => {
    root.querySelectorAll(selector).forEach((element) => {
      element.style.setProperty('top', 'auto', 'important');
      element.style.setProperty('bottom', targetBottom, 'important');
      element.style.setProperty('margin-bottom', '-5rem', 'important');
    });
  });
};

const positionSupportWidgetBox = () => {
  positionSupportWidgetBoxInRoot(document);

  document.querySelectorAll('*').forEach((element) => {
    if (element.shadowRoot) {
      positionSupportWidgetBoxInRoot(element.shadowRoot);
    }
  });
};

const hideSupportLauncherElements = () => {
  hideSupportLauncherElementsInRoot(document);

  document.querySelectorAll('*').forEach((element) => {
    if (element.shadowRoot) {
      hideSupportLauncherElementsInRoot(element.shadowRoot);
    }
  });
};

const ensureSupportLauncherHidden = () => {
  if (!document.getElementById(GHL_HIDE_LAUNCHER_STYLE_ID)) {
    const style = document.createElement('style');
    style.id = GHL_HIDE_LAUNCHER_STYLE_ID;
    style.textContent = `
      .widget-open-icon,
      .icon.widget-open-icon,
      [class*="widget-open-icon"],
      .lc_text_widget-trigger,
      [class*="lc_text_widget-trigger"],
      .lc-chat-widget,
      [data-testid="chat-widget-launcher"],
      [class*="chat-widget-launcher"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }

      .lc_text-widget--box,
      [class*="lc_text-widget--box"] {
        top: auto !important;
        bottom: ${SUPPORT_BOX_BOTTOM_DESKTOP} !important;
      }

      @media (max-width: 768px) {
        .lc_text-widget--box,
        [class*="lc_text-widget--box"] {
          top: auto !important;
          bottom: ${SUPPORT_BOX_BOTTOM_MOBILE} !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  hideSupportLauncherElements();
  positionSupportWidgetBox();
};

// Define las props que el componente acepta
const props = defineProps({
  menuOptions: Array,
});

const user = reactive({
  id: "",
  name: "",
  email: "",
  imageUrl: "",
});

onMounted(async () => {
  user.id = userStore.profile.id
  user.name = userStore.profile.name
  user.email = userStore.profile.email
  user.imageUrl = '/image/avatar/avatar_' + userStore.profile.genre + '.png'

  if (userStore.profile.image != null) {
    user.imageUrl = userStore.profile.image
  }

  // Cargar el contador de no leidas en background. Antes esto se hacia dentro
  // de NotificationSidebar.onMounted (incluso cuando estaba cerrado), y ademas
  // pedia la lista completa paginada; aqui solo traemos el count (payload
  // minimo) para pintar el badge del Navbar.
  notificationsApi.getUnreadNotificationsCount()
    .then(res => notificationsStore.setUnReadNotifications(res.data || 0))
    .catch(err => console.error('Error cargando unread notifications count (no critico):', err));

  // Estado de fichaje (Play/Pause). Antes se pedia desde AuthenticatedLayout
  // como parte del arranque; al ser solo un dato visual del Navbar, lo
  // movemos aqui (lazy-dedup a traves del store).
  userStore.ensureAttendanceStatus();

  ensureSupportLauncherHidden();

  supportLauncherObserver = new MutationObserver(() => {
    hideSupportLauncherElements();
    positionSupportWidgetBox();
  });

  supportLauncherObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  supportLauncherInterval = setInterval(() => {
    hideSupportLauncherElements();
    positionSupportWidgetBox();
  }, 600);
});


const userNavigation = [
  { name: t('mainMenu.profile'), hero_icon: 'UserCircle', path: '/user/profile/' + userStore.profile.id },
  { name: t('mainMenu.logout'), hero_icon: 'ArrowRightStartOnRectangle', path: '/logout' },
]

const employeeAttendance = () => {
  emit('attendanceChanged');
}

const toggleNotificationSidebar = () => {
  showNotifSidebar.value = !showNotifSidebar.value;
};

const handleCloseNotificationSidebar = () => {
  showNotifSidebar.value = false;

};

const ensureSupportChatScriptLoaded = () => {
  if (window.leadConnector?.chatWidget) {
    return Promise.resolve();
  }

  const existingScript = document.querySelector(`script[src="${GHL_CHAT_SCRIPT_SRC}"][data-widget-id="${GHL_CHAT_WIDGET_ID}"]`);
  if (existingScript) {
    return waitForSupportChatWidget();
  }

  const script = document.createElement('script');
  script.src = GHL_CHAT_SCRIPT_SRC;
  script.async = true;
  script.setAttribute('data-resources-url', GHL_CHAT_RESOURCES_URL);
  script.setAttribute('data-widget-id', GHL_CHAT_WIDGET_ID);

  return new Promise((resolve, reject) => {
    script.onload = async () => {
      try {
        await waitForSupportChatWidget();
        resolve();
      } catch (error) {
        reject(error);
      }
    };
    script.onerror = () => reject(new Error('Support chat script failed to load'));
    document.body.appendChild(script);
  });
};

const openSupportChat = async () => {
  if (isSupportChatLoading.value) {
    return;
  }

  try {
    isSupportChatLoading.value = true;
    ensureSupportLauncherHidden();
    await ensureSupportChatScriptLoaded();

    if (window.leadConnector?.chatWidget?.openWidget) {
      window.leadConnector.chatWidget.openWidget();
      hideSupportLauncherElements();
      positionSupportWidgetBox();
    }
  } catch (error) {
    console.error('Error al abrir el chat de soporte:', error);
  } finally {
    isSupportChatLoading.value = false;
  }
};

onUnmounted(() => {
  if (supportLauncherObserver) {
    supportLauncherObserver.disconnect();
    supportLauncherObserver = null;
  }

  if (supportLauncherInterval) {
    clearInterval(supportLauncherInterval);
    supportLauncherInterval = null;
  }
});


</script>

<template>
  <Disclosure as="nav" class="bg-blue-800" v-slot="{ open }">
    <div class="mx-auto max-w-12xl px-4 sm:px-6 lg:px-8">
      <div class="flex h-16 items-center justify-between">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <img class="h-8 w-8" :src="imagotipo" alt="Assetry" />
          </div>
          <div class="hidden md:block">
            <div class="ml-10 flex items-baseline space-x-4">
              <Menu as="div" class="relative ml-3" v-for="item in props.menuOptions" :key="item.name">
                <div>
                  <MenuButton
                    :class="[item.current ? 'bg-blue-900 text-white' : 'text-blue-300 hover:bg-blue-700 hover:text-white', 'rounded-md px-3 py-2 text-sm font-medium']">
                    <router-link v-if="item.path && $route.path !== item.path" :to="item.path">{{ item.name
                    }}</router-link>
                    <span v-else>{{ item.name }}</span>
                  </MenuButton>
                </div>
                <transition enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95">
                  <MenuItems v-if="item.children && item.children.length > 0"
                    class="absolute left-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none max-h-[calc(100vh-4rem)] overflow-y-auto">
                    <MenuItem v-for="child in item.children" :key="child.name" v-slot="{ active }">
                    <router-link :to="child.path" v-if="child.path && $route.path !== child.path"
                      :class="[active ? 'bg-blue-100' : '', 'flex items-center px-4 py-2 text-sm text-blue-950 hover:bg-blue-100']">
                      <HeroIcon v-if="child.hero_icon" :iconName="child.hero_icon"
                        class="h-5 w-5 sm:text-blue-400 text-white" />
                      <span class="ml-2">{{ child.name }}</span>
                    </router-link>
                    <span v-else :class="'flex items-center px-4 py-2 text-sm text-blue-950'">
                      <HeroIcon v-if="child.hero_icon" :iconName="child.hero_icon" class="h-5 w-5 text-blue-400" />
                      <span class="ml-2">{{ child.name }}</span>
                    </span>
                    </MenuItem>
                  </MenuItems>
                </transition>
              </Menu>
            </div>
          </div>
        </div>

        <div class="hidden md:flex items-center space-x-4">

          <!-- Marketplace -->
          <!-- <RouterLink v-if="userStore.profile.admin" to="/marketplace" class="relative rounded-full bg-blue-800 p-1 text-blue-400 hover:text-white">
            <span class="sr-only">Marketplace</span>
            <SquaresPlusIcon class="h-6 w-6" aria-hidden="true" />
          </RouterLink> -->
          
          <!-- Support Chat -->
          <button type="button" @click="openSupportChat"
            class="relative rounded-full bg-blue-800 p-1 text-blue-400 hover:text-white">
            <span class="sr-only">Open support chat</span>
            <HeroIcon iconName="ChatBubbleLeftRight" class="h-6 w-6 text-blue-400 cursor-pointer hover:text-white" />
          </button>

          <!-- Toggle Attendance -->
          <button type="button" @click="employeeAttendance" :disabled="layoutStore.isLoading"
            class="relative rounded-full bg-blue-800 p-1 text-blue-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed">
            <span class="sr-only">Toggle Attendance</span>
            <HeroIcon v-if="!userStore.attendanceStatus" iconName="Play"
              class="h-6 w-6 text-blue-400 cursor-pointer hover:text-white" />
            <HeroIcon v-if="userStore.attendanceStatus" iconName="Pause"
              class="h-6 w-6 text-blue-400 cursor-pointer hover:text-white" />
          </button>

          <!-- Updated notification bell for desktop with count from store -->
          <button type="button" @click="toggleNotificationSidebar"
            class="relative rounded-full bg-blue-800 p-1 text-blue-400 hover:text-white">
            <span class="sr-only">View notifications</span>
            <BellIcon class="h-6 w-6" aria-hidden="true" />
            <span v-if="notificationsStore.unreadCount > 0"
              class="absolute top-0 right-0 -mt-1 -mr-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-xs font-medium text-white">
              {{ notificationsStore.unreadCount }}
            </span>
          </button>

          <Menu as="div" class="relative ml-3">
            <div>
              <MenuButton
                class="relative flex max-w-xs items-center rounded-full bg-blue-800 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-800">
                <span class="sr-only">Open user menu</span>
                <img class="h-8 w-8 rounded-full" :src="user.imageUrl" alt="" />
              </MenuButton>
            </div>
            <transition enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95">
              <MenuItems
                class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <MenuItem v-for="item in userNavigation" :key="item.name" v-slot="{ active }">
                <router-link :to="item.path"
                  :class="[active ? 'bg-blue-100' : '', 'flex items-center px-4 py-2 text-sm text-blue-950 hover:bg-blue-100']"
                  tag="a">
                  <HeroIcon v-if="item.hero_icon" :iconName="item.hero_icon" class="h-5 w-5 text-blue-400" />
                  <span class="ml-2">{{ item.name }}</span>
                </router-link>
                </MenuItem>
              </MenuItems>
            </transition>
          </Menu>
        </div>

        <!-- Mobile menu button and attendance button -->
        <div class="-mr-2 flex items-center space-x-2 md:hidden">
          <!-- Support Chat -->
          <button type="button" @click="openSupportChat"
            class="relative rounded-full bg-blue-800 p-1 text-blue-400 hover:text-white">
            <span class="sr-only">Open support chat</span>
            <HeroIcon iconName="ChatBubbleLeftRight" class="h-6 w-6 text-blue-400 cursor-pointer hover:text-white" />
          </button>

          <button type="button" @click="employeeAttendance" :disabled="layoutStore.isLoading"
            class="relative rounded-full bg-blue-800 p-1 text-blue-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed">
            <span class="sr-only">Toggle Attendance</span>
            <HeroIcon v-if="!userStore.attendanceStatus" iconName="Play"
              class="h-6 w-6 text-blue-400 cursor-pointer hover:text-white" />
            <HeroIcon v-if="userStore.attendanceStatus" iconName="Pause"
              class="h-6 w-6 text-blue-400 cursor-pointer hover:text-white" />
          </button>

          <!-- Updated notification bell for mobile with count from store -->
          <button type="button" @click="toggleNotificationSidebar"
            class="relative rounded-full bg-blue-800 p-1 text-blue-400 hover:text-white">
            <span class="sr-only">View notifications</span>
            <BellIcon class="h-6 w-6" aria-hidden="true" />
            <span v-if="notificationsStore.unreadCount > 0"
              class="absolute top-0 right-0 -mt-1 -mr-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-xs font-medium text-white">
              {{ notificationsStore.unreadCount }}
            </span>
          </button>

          <DisclosureButton
            class="relative inline-flex items-center justify-center rounded-md bg-blue-800 p-2 text-blue-400 hover:bg-blue-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-800">
            <span class="sr-only">Open main menu</span>
            <Bars3Icon v-if="!open" class="block h-6 w-6" aria-hidden="true" />
            <XMarkIcon v-else class="block h-6 w-6" aria-hidden="true" />
          </DisclosureButton>
        </div>
      </div>
    </div>

    <DisclosurePanel class="md:hidden">
      <div class="space-y-1 px-2 pb-3 pt-2 sm:px-3 overflow-y-auto max-h-[calc(100vh-4rem)]">
        <div v-for="item in props.menuOptions" :key="item.name">
          <div v-if="item.children && item.children.length > 0" class="space-y-1">
            <Disclosure>
              <DisclosureButton
                class="flex justify-between w-full rounded-md px-3 py-2 text-base font-medium text-blue-300 hover:bg-blue-700 hover:text-white">
                <span>{{ item.name }}</span>
                <HeroIcon iconName="ChevronDown" class="h-5 w-5 text-blue-400" />
              </DisclosureButton>
              <DisclosurePanel class="space-y-1 pl-4 max-h-60 overflow-y-auto">
                <router-link v-for="child in item.children" :key="child.name" :to="child.path"
                  class="block rounded-md px-3 py-2 text-base font-medium text-blue-400 hover:bg-blue-700 hover:text-white">
                  {{ child.name }}
                </router-link>
              </DisclosurePanel>
            </Disclosure>
          </div>
          <router-link v-else :to="item.path"
            class="block rounded-md px-3 py-2 text-base font-medium text-blue-300 hover:bg-blue-700 hover:text-white">
            {{ item.name }}
          </router-link>
        </div>
      </div>
      <div class="border-t border-blue-700 pb-3 pt-4 overflow-y-auto max-h-[calc(100vh-4rem)]">
        <div class="flex items-center px-5">
          <div class="flex-shrink-0">
            <img class="h-10 w-10 rounded-full" :src="user.imageUrl" alt="" />
          </div>
          <div class="ml-3">
            <div class="text-base font-medium leading-none text-white">{{ user.name }}</div>
            <div class="text-sm font-medium leading-none text-blue-400">{{ user.email }}</div>
          </div>
          <button type="button"
            class="relative ml-auto flex-shrink-0 rounded-full bg-blue-800 p-1 text-blue-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-800">
            <span class="sr-only">View notifications</span>
            <BellIcon class="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <div class="mt-3 space-y-1 px-2">
          <DisclosureButton v-for="item in userNavigation" :key="item.name" as="a" :href="item.path"
            class="block rounded-md px-3 py-2 text-base font-medium text-blue-400 hover:bg-blue-700 hover:text-white">
            {{ item.name }}
          </DisclosureButton>
        </div>
      </div>
    </DisclosurePanel>
  </Disclosure>

  <!-- Navbar.vue -->
  <NotificationSidebar :show="showNotifSidebar" @close="handleCloseNotificationSidebar" />
</template>

<style scoped>
/* styles.css o en un <style scoped> dentro de tu componente Vue */
.icon-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

.icon-size {
  width: 24px;
  height: 24px;
}

.icon-color-blue {
  fill: #ffffff;
}

.icon-hover {
  cursor: pointer;
  transition: fill 0.3s ease;
}

.icon-hover:hover {
  fill: #ffffff;
}
</style>
